import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { FaHeart, FaTrash, FaShoppingCart, FaRegHeart } from "react-icons/fa";
import { fetchFavorites, toggleFavorite } from "../../redux/actions/favoriteAction";
import { addToCart, fetchCartItems } from "../../redux/actions/cartAction";
import { toast } from "react-toastify";
import { useDarkMode } from "../../App";
import "./Favorites.css";

const Favorites = () => {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();

    const { items: favorites, status, error } = useSelector((state) => state.favorite);

    const token = localStorage.getItem("token") || JSON.parse(localStorage.getItem("userTools") || "{}")?.token;
    const isSocial = token === 'social_login_placeholder';

    useEffect(() => {
        if (token && !isSocial) {
            dispatch(fetchFavorites({ language: i18n.language }));
        }
    }, [dispatch, i18n.language, token, isSocial]);

    // Debugging: Check favorites data
    useEffect(() => {
        console.log("Favorites data:", favorites);
    }, [favorites]);

    const handleRemoveFavorite = async (productId) => {
        const resultAction = await dispatch(toggleFavorite({ productId, language: i18n.language }));
        if (toggleFavorite.fulfilled.match(resultAction)) {
            toast.success(t("favorites.removed"));
            dispatch(fetchFavorites({ language: i18n.language }));
        } else {
            toast(t("favorites.error"));
        }
    };

    const handleAddToCart = (product) => {
        dispatch(addToCart({ 
            product_id: product.id, 
            qty: 1, 
            language: i18n.language 
        })).unwrap()
        .then(() => {
            toast.success(t("cart.addedSuccessfully"));
            dispatch(fetchCartItems(i18n.language));
        })
        .catch((err) => toast(err.message || t("cart.addError")));
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    return (
        <div className={`favorites-container ${isDarkMode ? "dark" : ""}`}>
            <div className="container py-5">
                <header className="favorites-header mb-5">
                    <motion.h1 
                        className="favorites-title mb-3"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d' }}
                    >
                        {t("favorites.title")}
                    </motion.h1>
                    <p className="text-muted" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                        {t("favorites.subtitle")}
                    </p>
                </header>

                {status === "loading" ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t("favorites.loading")}</span>
                        </div>
                        <p className="mt-3 text-muted">{t("favorites.loading")}</p>
                    </div>
                ) : favorites && favorites.length > 0 ? (
                    <motion.div 
                        className="favorites-grid"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <AnimatePresence>
                            {favorites.map((product) => (
                                <motion.div 
                                    key={product.id}
                                    className={`favorite-card ${isDarkMode ? 'dark-mode' : ''}`}
                                    variants={itemVariants}
                                    exit={{ scale: 0.8, opacity: 0 }}
                                    layout
                                >
                                    <div className="favorite-img-wrapper position-relative">
                                        <img 
                                            src={product.image || product.images?.[0] || "/Frame.png"} 
                                            alt={product.name} 
                                            className="favorite-img"
                                            onError={(e) => { e.target.src = "/Frame.png"; }}
                                        />
                                        <div className="favorite-overlay">
                                            <button 
                                                className="fav-action-btn heart-btn"
                                                onClick={() => handleRemoveFavorite(product.id)}
                                                title={t("favorites.remove")}
                                            >
                                                <FaHeart />
                                            </button>
                                            <button 
                                                className="fav-action-btn cart-btn"
                                                onClick={() => handleAddToCart(product)}
                                                title={t("favorites.addToCart")}
                                            >
                                                <FaShoppingCart />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="favorite-info">
                                        <Link 
                                            to={`/productDetails/${product.id}`} 
                                            className="favorite-name"
                                            style={{ color: isDarkMode ? 'var(--color-text)' : '#333' }}
                                        >
                                            {product.name}
                                        </Link>
                                        {/* Product Code */}
                                        {/* Product Code */}
                                        {(product.code || product.sku || product.item_code || product.product?.code || product.product?.sku || product.barcode) && (
                                            <div className="product-code mt-1" style={{ fontSize: '0.85rem', color: isDarkMode ? 'var(--color-muted)' : '#6c757d' }}>
                                                {t('product.code')}: {product.code || product.sku || product.item_code || product.product?.code || product.product?.sku || product.barcode}
                                            </div>
                                        )}
                                        <div className="favorite-price mt-2">
                                            <span className="price-value" style={{ color: '#ff3e3e', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                                {product.final_price || product.price}
                                            </span>
                                            <span className="currency ms-2" style={{ color: isDarkMode ? 'var(--color-muted)' : '#999' }}>
                                                {t("favorites.currency")}
                                            </span>
                                            {product.final_price && product.final_price < product.price && (
                                                <span className="old-price ms-2" style={{ color: isDarkMode ? 'var(--color-muted)' : '#999', textDecoration: 'line-through' }}>
                                                    {product.price} {t("favorites.currency")}
                                                </span>
                                            )}
                                        </div>
                                        <div className="favorite-actions mt-3 gap-2 d-flex">
                                            <button 
                                                className="add-to-cart-btn flex-grow-1"
                                                onClick={() => handleAddToCart(product)}
                                                style={{
                                                    background: 'linear-gradient(135deg, #ff3e3e 0%, #ff1a1a 100%)',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '10px 15px',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontWeight: '600',
                                                    transition: 'transform 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                            >
                                                <FaShoppingCart className="me-2" />
                                                {t("favorites.addToCart")}
                                            </button>
                                            <button 
                                                className="remove-btn"
                                                onClick={() => handleRemoveFavorite(product.id)}
                                                title={t("favorites.remove")}
                                                style={{
                                                    background: isDarkMode ? 'rgba(255, 62, 62, 0.1)' : '#fff5f5',
                                                    color: '#ff3e3e',
                                                    border: '1px solid #ff3e3e',
                                                    padding: '10px 15px',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = '#ff3e3e';
                                                    e.target.style.color = 'white';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = isDarkMode ? 'rgba(255, 62, 62, 0.1)' : '#fff5f5';
                                                    e.target.style.color = '#ff3e3e';
                                                }}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                ) : (
                    <motion.div 
                        className="empty-favorites text-center py-5"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        <FaRegHeart className="empty-icon mb-4" style={{ fontSize: '4rem', color: '#ccc' }} />
                        <h2 style={{ color: isDarkMode ? 'var(--color-text)' : '#333', marginBottom: '1rem' }}>
                            {t("favorites.emptyTitle")}
                        </h2>
                        <p style={{ color: isDarkMode ? 'var(--color-muted)' : '#999', marginBottom: '2rem' }}>
                            {t("favorites.emptyDesc")}
                        </p>
                        <Link 
                            to="/categories" 
                            className="submit-btn d-inline-block"
                            style={{
                                background: 'linear-gradient(135deg, #000c3d 0%, #1a235e 100%)',
                                color: 'white',
                                padding: '12px 30px',
                                borderRadius: '25px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            {t("favorites.startShopping")}
                        </Link>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default Favorites;
